/**
 * Events CSV import: preview (read-only) and create (one event per call) against migration 980's RPCs.
 *
 * Both RPCs run AS THE CALLER (SECURITY INVOKER): the create is the same INSERT INTO events the create wizard sends,
 * so events RLS and every events trigger apply, and the range-night host link goes through the same
 * set_event_org_associations RPC. The database refuses anyone who is not an active superadmin / admin / manager /
 * staff user.
 *
 * Nothing here sends email: imported events are created "scheduled" + setup_incomplete, and (unlike the wizard)
 * the import does NOT call notifyEventNeedsConfiguration — one events@ email per imported row would be a flood,
 * and the importer is the events team.
 */
import { supabase } from "./supabase";
import { normalizeZip5, resolveEventTimezoneByZip } from "../utils/zipTimezone";
import type {
  NormalizedEventRow,
  ParsedEventRow,
} from "../utils/eventCsvImport";

export interface PlanDuplicate {
  event_id: string;
  title: string;
  status: string;
  start_local: string;
  location_name: string | null;
}

export interface EventImportPlan {
  ok: boolean;
  errors: string[];
  warnings: string[];
  event_id: string | null;
  row_key: string | null;
  /** Digest of everything create would write. Create sends it back and the database refuses the row if it changed. */
  plan_digest: string;
  already_imported: {
    event_id: string;
    title: string;
    status: string;
    is_deleted: boolean;
  } | null;
  duplicates: PlanDuplicate[];
  organization: {
    id: string;
    name: string;
    facility_id: string | null;
    type: string | null;
  } | null;
  venue: {
    name: string | null;
    address_line1: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    source: "row" | "location" | "organization";
  };
  event: {
    type: string;
    title: string;
    start_date: string | null;
    end_date: string | null;
    start_time_local: string | null;
    end_time_local: string | null;
    timezone: string | null;
    max_attendees: number | null;
    entry_fee: number | null;
    state: string | null;
  };
  row_number: number;
}

export type PreviewStatus =
  | "ready"
  | "error"
  | "duplicate"
  | "already_imported";

export interface PreviewedRow {
  parsed: ParsedEventRow;
  /** The row actually sent to the database (time zone filled from the venue when the CSV left it blank). */
  sent: NormalizedEventRow;
  plan: EventImportPlan | null;
  status: PreviewStatus;
  errors: string[];
  warnings: string[];
  timezoneSource: "row" | "zip" | "state" | null;
}

const PREVIEW_CHUNK = 100;

export function isImportUnavailable(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  return code === "PGRST202" || code === "42883";
}

async function previewChunk(
  rows: Array<NormalizedEventRow & { row_number: number }>,
): Promise<EventImportPlan[]> {
  const { data, error } = await supabase.rpc("event_csv_import_preview", {
    p_rows: rows,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as EventImportPlan[];
}

async function previewAll(
  rows: Array<NormalizedEventRow & { row_number: number }>,
): Promise<Map<number, EventImportPlan>> {
  const out = new Map<number, EventImportPlan>();
  for (let i = 0; i < rows.length; i += PREVIEW_CHUNK) {
    const plans = await previewChunk(rows.slice(i, i + PREVIEW_CHUNK));
    plans.forEach((p) => out.set(p.row_number, p));
  }
  return out;
}

function statusOf(
  plan: EventImportPlan | null,
  errors: string[],
): PreviewStatus {
  if (errors.length > 0 || !plan || !plan.ok) {
    return "error";
  }
  if (plan.already_imported) {
    return "already_imported";
  }
  if (plan.duplicates.length > 0) {
    return "duplicate";
  }
  return "ready";
}

/**
 * Preview every row: rows that failed the browser checks are not sent. Pass 1 resolves the host and its venue;
 * a range-night row with no venue and no time zone gets its zone from the host's ZIP / state and is planned again
 * (pass 2), so start/end, "in the past" and duplicates are judged in the right zone. Writes nothing.
 */
export async function previewEventImport(
  parsedRows: ParsedEventRow[],
): Promise<PreviewedRow[]> {
  const sendable = parsedRows.filter((p) => p.errors.length === 0);
  const sent = new Map<number, NormalizedEventRow>();
  const tzSource = new Map<number, PreviewedRow["timezoneSource"]>();
  sendable.forEach((p) => {
    sent.set(p.rowNumber, { ...p.row });
    tzSource.set(p.rowNumber, p.timezoneSource);
  });
  const payload = () =>
    sendable.map((p) => ({
      ...(sent.get(p.rowNumber) as NormalizedEventRow),
      row_number: p.rowNumber,
    }));

  let plans = sendable.length
    ? await previewAll(payload())
    : new Map<number, EventImportPlan>();

  let replan = false;
  sendable.forEach((p) => {
    const row = sent.get(p.rowNumber) as NormalizedEventRow;
    const plan = plans.get(p.rowNumber);
    if (!row.timezone && plan) {
      // EVT-08b: the host venue's ZIP only (no state fallback).
      const r = resolveEventTimezoneByZip(plan.venue?.postal_code);
      if (r) {
        // The zone comes from the venue the server resolved (the host's), and the row says so: the database refuses
        // it at pass 2 / create if that venue has changed since (audit 73915ddd F2).
        row.timezone = r.timezone;
        row.timezone_derived = true;
        row.timezone_from_zip = normalizeZip5(plan.venue?.postal_code);
        row.timezone_from_state = plan.venue?.state ?? null;
        tzSource.set(p.rowNumber, r.source);
        replan = true;
      }
    }
  });
  if (replan) {
    plans = await previewAll(payload());
  }

  // In-file duplicates across preview chunks (the server also checks within each chunk).
  const firstRowFor = new Map<string, number>();
  return parsedRows.map((p) => {
    const plan = plans.get(p.rowNumber) ?? null;
    const errors = [...p.errors, ...(plan?.errors ?? [])];
    if (plan?.ok && plan.event_id) {
      const first = firstRowFor.get(plan.event_id);
      if (
        first !== undefined &&
        first !== p.rowNumber &&
        !errors.some((e) => e.startsWith("same event as row"))
      ) {
        errors.push(`same event as row ${first}`);
      } else if (first === undefined) {
        firstRowFor.set(plan.event_id, p.rowNumber);
      }
    }
    const uniqueErrors = [...new Set(errors)];
    return {
      parsed: p,
      sent: sent.get(p.rowNumber) ?? p.row,
      plan,
      status: statusOf(plan, uniqueErrors),
      errors: uniqueErrors,
      warnings: [...new Set([...p.warnings, ...(plan?.warnings ?? [])])],
      timezoneSource: tzSource.get(p.rowNumber) ?? p.timezoneSource,
    };
  });
}

export type CreateStatus =
  | "created"
  | "already_imported"
  | "skipped_duplicate"
  /** The plan resolved at create differs from the previewed one (e.g. host address changed): nothing written. */
  | "plan_changed"
  | "error";

export interface CreateResult {
  row_number: number;
  status: CreateStatus;
  event_id?: string;
  title?: string;
  errors?: string[];
  duplicates?: PlanDuplicate[];
}

/** Create ONE event (its own short transaction). Never throws: a failure comes back as status "error". */
export async function createImportedEvent(args: {
  batchId: string;
  fileName: string;
  rowNumber: number;
  row: NormalizedEventRow;
  /** plan_digest from the preview the operator approved; the database refuses the row if its plan changed since. */
  expectedDigest: string;
  allowDuplicate: boolean;
}): Promise<CreateResult> {
  try {
    const { data, error } = await supabase.rpc("event_csv_import_create", {
      p_batch_id: args.batchId,
      p_file_name: args.fileName,
      p_row_number: args.rowNumber,
      p_row: args.row,
      p_expected_digest: args.expectedDigest,
      p_allow_duplicate: args.allowDuplicate,
    });
    if (error) {
      return {
        row_number: args.rowNumber,
        status: "error",
        errors: [error.message],
      };
    }
    return data as CreateResult;
  } catch (e) {
    return {
      row_number: args.rowNumber,
      status: "error",
      errors: [e instanceof Error ? e.message : String(e)],
    };
  }
}
